import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { format, isFuture, isToday, isValid, parseISO } from "date-fns";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
import { useCookies } from "react-cookie";
// hooks
import { useTransportLanguageRefresh } from "../../../common/hooks/useTransportLanguageRefresh";
// components
import AppDatePickerCustom from "../../../components/AppDatePickerCustom";
import { TransportRowAction } from "../shared/transportRow";
import AirportBusPassengersForm from "./AirportBusPassengersForm";
import AirportBusSummary from "./AirportBusSummary";
import AppBookingError from "../../../components/AppBookingError";
import PaymentModal from "../../../components/Payment/PaymentModal";
import AppButton from "../../../components/ui/AppButton";
import TransportProviderHeader from "../../../components/TransportProviderContent/TransportProviderHeader";
import LastMileInfo from "../../../components/LastMileInfo";
// helpers
import { checkForEmptyNames } from "../shared/helpers/checkForEmptyNames";
import { resolveTransportStopCoords } from "../shared/helpers/resolveTransportStopCoords";
import { getAirlineAirportBusArrivalStop } from "./helpers/getAirlineAirportBusArrivalStop";
import type { AirportBusProviderConfig } from "./config/types";
import { getAirportBusConfirmationPath, getAirportBusPathSuffix } from "./config/types";
import { navigateBackOrToMain } from "../../../common/utils/helpers/navigateBackOrToMain";
import {
  buildAirlineNavigationState,
  clearPersistedAirlineRouterState,
  persistAirlineFromNavigationState,
  readAirlineTransportAddressesFromNavigationState,
  resolveTransportAirlineApiContext
} from "../../../common/utils/transport/airlineTransportContext";
import { pushTransportBookMarketplaceContext } from "../../../common/utils/analytics/pushTransportBookMarketplaceContext";
// actions
import { useActions } from "../../../common/hooks/useActions";
import { airportBusActionCreators } from "./airportBusModule";
import { errorFieldActionCreators } from "../../../common/store/modules/errorFieldModule";
// icons
import { dateIcon } from "../../../common/vector";

interface AirportBusProps {
  config: AirportBusProviderConfig;
}

export default function AirportBus({ config }: AirportBusProps) {
  const theme = useTheme();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [cookies] = useCookies(["cookieAccepted"]);
  const language = localStorage.getItem("i18nextLng") || "en";
  const cssBlock = "airport-bus";
  const dataAttrPrefix = config.slug;

  useLayoutEffect(() => {
    persistAirlineFromNavigationState(location.state);
  }, [location.state]);

  const restoredNavState = useMemo(() => buildAirlineNavigationState(location.state), [location.state]);

  const transportNavState = useMemo(() => restoredNavState ?? location.state, [restoredNavState, location.state]);

  const transportContext = useMemo(() => resolveTransportAirlineApiContext(transportNavState), [transportNavState]);
  const isAirlineTransferHub = transportContext?.mode === "airline";

  const transportAddresses = useMemo(
    () => readAirlineTransportAddressesFromNavigationState(transportNavState),
    [transportNavState]
  );

  const airlineSharedRoute = useMemo(() => {
    const nav = transportNavState as
      | {
          meta?: { source?: string };
          shared?: {
            departureDate?: string | null;
            milesAndMore?: string;
          };
        }
      | null
      | undefined;

    const isAirlineSource = nav?.meta?.source === "airline";

    return {
      ...transportAddresses,
      milesAndMore: isAirlineSource ? nav?.shared?.milesAndMore ?? "" : "",
      departureDate: isAirlineSource ? nav?.shared?.departureDate ?? null : null
    };
  }, [transportNavState, transportAddresses]);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const platformType = useSelector((state: any) => state.global.platformType);
  const bookingResponseData = useSelector((state: any) => state.airportBus.bookingResponseData);
  const customRouteCode = useSelector((state: any) => state.airportBus.customRouteCode);
  const infoError = useSelector((state: any) => state.airportBus.infoError);
  const providerData = useSelector((state: any) => state.airportBus.providerData);
  const passengerList = useSelector((state: any) => state.airportBus.passengerList);
  const errorPrebook = useSelector((state: any) => state.airportBus.errorPrebook);
  const prebookPriceInfo = useSelector((state: any) => state.airportBus.prebookPriceInfo);
  const bookingError = useSelector((state: any) => state.airportBus.bookingError);
  const showPaymentModal = useSelector((state: any) => state.airportBus.showPaymentModal);
  const globalLoader = useSelector((state: any) => state.global.globalLoader);
  const lastMileRoute = useSelector((state: any) => state.airportBus.lastMileRoute);
  const isLastMileRouteLoading = useSelector((state: any) => state.airportBus.isLastMileRouteLoading);
  const lastMileRouteError = useSelector((state: any) => state.airportBus.lastMileRouteError);

  const initProvider = useActions(airportBusActionCreators?.initAirportBusProviderAction, []);
  const getAirportBusInfo = useActions(airportBusActionCreators?.getAirportBusInfoAction, []);
  const getPricePrebook = useActions(airportBusActionCreators?.getPriceAirportBusPrebookAction, []);
  const buyTicket = useActions(airportBusActionCreators?.buyAirportBusTicketAction, []);
  const resetState = useActions(airportBusActionCreators?.resetAirportBusStateAction, []);
  const togglePaymentModal = useActions(airportBusActionCreators?.togglePaymentModalAction, []);
  const toggleBookingError = useActions(airportBusActionCreators?.toggleBookingErrorAction, []);
  const getLastMileRoute = useActions(airportBusActionCreators?.getAirportBusLastMileRouteAction, []);
  const resetErrorField = useActions(errorFieldActionCreators?.resetErrorFieldAction, []);

  useEffect(() => {
    initProvider(config);
  }, [initProvider, config]);

  useEffect(() => {
    return () => {
      togglePaymentModal(false);
      resetState();
      resetErrorField();
      clearPersistedAirlineRouterState();
    };
  }, [togglePaymentModal, resetState, resetErrorField]);

  useEffect(() => {
    if (tenantInfo) {
      getAirportBusInfo(config, transportContext);
    }
  }, [tenantInfo, transportContext, getAirportBusInfo, config]);

  useEffect(() => {
    if (!airlineSharedRoute.departureDate) {
      return;
    }

    const parsedDate = parseISO(airlineSharedRoute.departureDate);
    if (isValid(parsedDate)) {
      setSelectedDate(parsedDate);
    }
  }, [airlineSharedRoute.departureDate]);

  // The arrival stop is auto-selected server-side (nearest-of-3). The airline search already returned it,
  // so prefer that; fall back to the estimation response for direct/hotel entries into the page.
  const airlineArrivalStop = useMemo(() => getAirlineAirportBusArrivalStop(transportNavState), [transportNavState]);
  const arrivalStop = airlineArrivalStop ?? prebookPriceInfo?.details?.destination;
  const destinationPlaceId = airlineSharedRoute.destinationAddressId;
  const finalDestinationName = airlineSharedRoute.destinationAddress;
  const nearestStopName = arrivalStop?.name ?? null;
  const arrivalStopCoords = useMemo(() => resolveTransportStopCoords(arrivalStop), [arrivalStop]);

  useEffect(() => {
    if (!isAirlineTransferHub || !destinationPlaceId || arrivalStopCoords === null) {
      return;
    }

    getLastMileRoute({
      departureLatitude: arrivalStopCoords.lat,
      departureLongitude: arrivalStopCoords.lng,
      destinationPlaceId,
      travelMode: "walking"
    });
  }, [isAirlineTransferHub, destinationPlaceId, arrivalStopCoords, getLastMileRoute]);

  useTransportLanguageRefresh({
    language,
    navigate,
    pathSuffix: getAirportBusPathSuffix(config.slug),
    getNavigationState: () => buildAirlineNavigationState(locationRef.current.state),
    iataCode: transportContext.mode === "airline" ? transportContext.iataCode : null
  });

  const isDateValid = useCallback((): boolean => isToday(selectedDate) || isFuture(selectedDate), [selectedDate]);

  const handleSeePriceBtn = () => {
    setShowValidationErrors(true);

    if (!isDateValid() || checkForEmptyNames(passengerList)) {
      return;
    }

    getPricePrebook(
      config,
      {
        start_date: format(selectedDate, "yyyy-MM-dd"),
        offer_code: customRouteCode,
        milesAndMore: airlineSharedRoute.milesAndMore,
        departureAddress: airlineSharedRoute.departureAddress,
        departureAddressId: airlineSharedRoute.departureAddressId,
        destinationAddress: airlineSharedRoute.destinationAddress,
        destinationAddressId: airlineSharedRoute.destinationAddressId
      },
      transportContext
    );
  };

  const handleBuyTicketBtn = () => {
    pushTransportBookMarketplaceContext({
      cookieAccepted: cookies.cookieAccepted,
      platformType,
      tenantInfo,
      offerCode: customRouteCode ?? null
    });

    buyTicket(
      config,
      {
        start_date: Math.round(+new Date(selectedDate) / 1000),
        offer_code: customRouteCode
      },
      navigate,
      transportContext
    );
  };

  const renderPrebookErrorMsg = () => {
    if (isDateValid() && !errorPrebook) {
      return null;
    }

    let errors: Array<any> = [];
    if (!isDateValid()) {
      errors.push({ errorMessage: t("transport.timeErrorMsg") });
    }
    if (errorPrebook?.fields) {
      errors = errors.concat(errorPrebook.fields);
    }
    if (!errors.length) {
      errors.push({ errorMessage: t("transport.prebookCustomError") });
    }

    return errors.map((error: any, i: number) => (
      <div key={i} className={`${cssBlock}__error-msg`}>
        {error?.field && `${error?.field} -`} {error?.errorMessage}
      </div>
    ));
  };

  const getDisabledStyleForTicketsForm = () => {
    if (prebookPriceInfo) {
      return { opacity: 0.4, pointerEvents: "none" as const };
    }
  };

  return (
    <Grid
      className={`${cssBlock} grid-box`}
      item
      sx={{ flexDirection: "column", flex: 1, margin: 0, padding: 0 }}
      mt={{ xs: 1, sm: 3 }}
      mb={{ xs: 4, sm: 5 }}
    >
      {bookingError ? (
        <AppBookingError
          dataAttribute={`${dataAttrPrefix}-booking-error-button`}
          onButtonClick={() => toggleBookingError(false)}
          title={t("bookingForm.error.title")}
          subtitle={t("bookingForm.error.message")}
        />
      ) : (
        <>
          <TransportProviderHeader
            title={providerData?.provider_title ?? providerData?.title ?? config.fallbackDisplayName}
            shortDescription={providerData?.provider_description}
            providerLogo={providerData?.provider_logo}
            titleClassName={`${cssBlock}__title`}
          />

          {customRouteCode && !infoError ? (
            <Grid container>
              <Grid item md={6} xs={12} sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ ...getDisabledStyleForTicketsForm() }}>
                  <TransportRowAction
                    label={t("transport.departureDate")}
                    icon={dateIcon?.default}
                    actionComponent={
                      <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                        <AppDatePickerCustom
                          selectedDate={selectedDate}
                          setSelectedDate={date => date != null && setSelectedDate(date)}
                          minDate={new Date()}
                        />
                      </Grid>
                    }
                  />
                </Box>
                {isAirlineTransferHub && finalDestinationName ? (
                  <Box mt={{ xs: 3, sm: 4 }} pl={{ xs: 2, sm: 4 }}>
                    <LastMileInfo
                      title={t("transport.lastMile.title")}
                      icon={<DirectionsWalkIcon fontSize="small" />}
                      destinationName={finalDestinationName}
                      stopName={nearestStopName}
                      distanceMeters={lastMileRoute?.distance_meters ?? null}
                      durationSeconds={lastMileRoute?.duration_seconds ?? null}
                      googleMapsUrl={lastMileRoute?.google_maps_url ?? null}
                      isLoading={isLastMileRouteLoading}
                      error={lastMileRouteError}
                      mapsLinkBelow
                    />
                  </Box>
                ) : null}
              </Grid>
              <Grid
                item
                md={6}
                xs={12}
                mt={{ sm: 6, xs: 6, md: 0 }}
                sx={{ ...getDisabledStyleForTicketsForm() }}
                position={"relative"}
              >
                <div className="vertical-divider" />
                <Box pl={{ xs: 2, sm: 6 }} pr={{ xs: 2, sm: 4 }}>
                  <AirportBusPassengersForm config={config} showValidationErrors={showValidationErrors} />
                  {renderPrebookErrorMsg()}
                  <Grid
                    container={isAirlineTransferHub}
                    item={!isAirlineTransferHub}
                    xs={12}
                    sm={isAirlineTransferHub ? undefined : 4}
                    sx={{
                      ...(isAirlineTransferHub ? { display: "flex", alignItems: "stretch", gap: 2 } : {})
                    }}
                  >
                    {isAirlineTransferHub && (
                      <Grid item sx={{ flex: 1 }}>
                        <AppButton
                          label={t("transport.goBack")}
                          onClick={() => navigateBackOrToMain(navigate, { language })}
                          disableRipple={true}
                          customBackgroundColor={theme.palette.primary.dark}
                          customHoverColor={theme.palette.primary.dark}
                          fontSize="14px"
                          dataAttribute={`${dataAttrPrefix}-go-back`}
                        />
                      </Grid>
                    )}

                    <Grid item sx={{ ...(isAirlineTransferHub ? { flex: 1 } : {}) }}>
                      <AppButton
                        label={t("transport.seePriceBtn")}
                        onClick={handleSeePriceBtn}
                        disableRipple={true}
                        customBackgroundColor={tenantInfo?.style?.button?.color}
                        customActiveColor={tenantInfo?.style?.button?.active}
                        customHoverColor={tenantInfo?.style?.button?.hover}
                        customClickColor={tenantInfo?.style?.button?.click}
                        fontSize="14px"
                        dataAttribute={`${dataAttrPrefix}-see-price`}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} mt={{ xs: 4, sm: 6 }} ml={{ xs: 2, sm: 5, md: 6 }} mr={{ xs: 2, sm: 5, md: 6 }}>
                {prebookPriceInfo && (
                  <AirportBusSummary
                    config={config}
                    onBuyTicket={handleBuyTicketBtn}
                    cancellationPolicy={providerData?.cancellation_policy}
                  />
                )}
              </Grid>
            </Grid>
          ) : (
            !globalLoader && (
              <Grid item xs={12} pb={4}>
                <Box sx={{ fontSize: 18 }}>{t("transport.notAvailable")}</Box>
              </Grid>
            )
          )}
        </>
      )}
      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          bookingResponseData={bookingResponseData}
          togglePaymentModal={togglePaymentModal}
          redirectUrl={getAirportBusConfirmationPath(config.slug)}
        />
      )}
    </Grid>
  );
}
